const BASE_URL = process.env.BUN_PUBLIC_API_BASE_URL;

export interface Vehicle {
  vehicle_id: string;
  user_id: string;
  username: string;
  make: string;
  model: string;
  year: number;
  vehicle_type: "car" | "motorcycle";
  nickname: string | null;
  mod_list: string | null;
  photo_url: string | null;
  horsepower: number | null;
  drive: "FWD" | "RWD" | "AWD" | null;
  transmission: "A" | "M" | null;
  engine_position: "front" | "center" | "rear" | null;
  created_at: string;
  updated_at: string;
}

export interface CreateVehiclePayload {
  make: string;
  model: string;
  year: number;
  photo_content: string;
  vehicle_type?: "car" | "motorcycle";
  nickname?: string;
  mod_list?: string;
  horsepower?: number;
  drive?: "FWD" | "RWD" | "AWD";
  transmission?: "A" | "M";
  engine_position?: "front" | "center" | "rear";
}

export interface UpdateVehiclePayload {
  make: string;
  model: string;
  year: number;
  vehicle_type?: "car" | "motorcycle";
  nickname?: string | null;
  mod_list?: string | null;
  horsepower?: number | null;
  drive?: "FWD" | "RWD" | "AWD" | null;
  transmission?: "A" | "M" | null;
  engine_position?: "front" | "center" | "rear" | null;
}

class VehiclesProvider {
  private async request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
      throw new Error("Unauthorized");
    }

    if (!res.ok) {
      const text = await res.text().catch(() => "Unknown error");
      throw new Error(text || `HTTP ${res.status}`);
    }

    const text = await res.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  create(payload: CreateVehiclePayload): Promise<{ id: string }> {
    return this.request<{ id: string }>("/v1/vehicles", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  uploadPhoto(vehicleId: string, content: string): Promise<string> {
    return this.request<string>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}/photo`,
      {
        method: "POST",
        body: JSON.stringify({ content }),
      }
    );
  }

  getByUser(userId: string): Promise<Vehicle[]> {
    return this.request<Vehicle[]>(
      `/v1/vehicles/user/${encodeURIComponent(userId)}`
    );
  }

  getById(vehicleId: string): Promise<Vehicle> {
    return this.request<Vehicle>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}`
    );
  }

  update(vehicleId: string, payload: UpdateVehiclePayload): Promise<string> {
    return this.request<string>(`/v1/vehicles/${encodeURIComponent(vehicleId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  delete(vehicleId: string): Promise<string> {
    return this.request<string>(
      `/v1/vehicles/${encodeURIComponent(vehicleId)}`,
      { method: "DELETE" }
    );
  }
}

export const vehiclesProvider = new VehiclesProvider();
